import { mapActions } from 'vuex'

const EARTH_RADIUS_METERS = 6371008.8;
const GOOD_ACCURACY_METERS = 30;   // leitura boa o bastante: para de refinar
const MAX_REFINING_MS = 20000;     // tempo máximo refinando a localização

export default {
  props: {
    embedded: { type: Boolean, default: false },
    title: { type: String, default: 'Registro de Presença' },
  },
  data: () => ({
    loading: false,
    snackbar: false,
    message: '',
    error: false,
    errors: [],
    selection: 1,
    token: null,
    loadingLogout: false,
    teacher: null,
    institution: null,
    showMark: false,
    loadingMark: false,
    sheet: null,
    // Localização do professor (null até a primeira leitura) e dados do Checkin (cerca eletrônica)
    position: null,
    locationState: 'idle', // idle | locating | ok | denied | unavailable | error
    refining: false,
    watchId: null,
    watchTimer: null,
    checkin: null,
    downloadingId: null,
    showReceipt: false,
    receipt: null,
    clockStatus: null,
    serverTime: null,
    sheet: {
      id: 1,
      items: [ { date: '2022-08-01' } ]
    },
    isBlocked: false,
    currentToken: null,
  }),
  computed: {
    
    teacherId: function() {
      return this.teacher ? this.teacher.id : 0;
    },

    formatedDate: function() {
      return new Date().toLocaleDateString();
    },

    /**
     * Verifica se o relógio está sincronizado com a HLB.
     */
    isClockValid: function() {
      return this.clockStatus === 'OK';
    },

    /**
     * Verifica se o usuário pode efetuar marcação de ponto.
     * Bloqueado se relógio inválido ou sem itens na folha.
     */
    canMark: function() {
      return this.isClockValid && this.sheet && this.sheet.items && this.sheet.items.length > 0 && this.locationAllowsMark;
    },

    /**
     * Cerca eletrônica (metros) configurada para a instituição; nulo = localização não é validada.
     */
    fence: function() {
      return this.checkin && this.checkin.electric_fence ? Number(this.checkin.electric_fence) : null;
    },

    /**
     * Aulas de hoje (disciplina e turma) do item do dia, vindas dos dados do Checkin.
     */
    todayClasses: function() {
      return this.checkin && this.checkin.items && this.checkin.items.length > 0 ? this.checkin.items[0].classes : [];
    },

    /**
     * Marcações de hoje (com NSR e link do comprovante), na ordem em que foram registradas.
     */
    todayRegisters: function() {
      return this.checkin && this.checkin.items && this.checkin.items.length > 0 ? this.checkin.items[0].registers : [];
    },

    /**
     * Configuração "Mostrar prova de localização": exibe o mapa com o raio da cerca eletrônica.
     */
    showProof: function() {
      return !!(this.checkin && this.checkin.show_localization_proof);
    },

    /**
     * O bloco de localização aparece com a cerca eletrônica configurada ou com a prova de localização ligada.
     */
    showLocationBlock: function() {
      return !!this.fence || this.showProof;
    },

    /**
     * Cor do raio no mapa: verde dentro, vermelho fora, laranja com leitura imprecisa, azul sem cerca.
     */
    mapStatus: function() {
      if (!this.fence) {
        return 'neutral';
      }

      if (this.lowAccuracy) {
        return 'warning';
      }

      return this.insideFence ? 'ok' : 'error';
    },

    companyCoordinates: function() {
      return this.checkin && this.checkin.company ? this.checkin.company.coordinates : null;
    },

    distance: function() {
      if (!this.position || !this.companyCoordinates) {
        return null;
      }

      return Math.round(this.haversine(
        this.position.lat, this.position.lng,
        this.companyCoordinates.lat, this.companyCoordinates.lng
      ));
    },

    /**
     * Margem de erro do GPS maior que o raio: a leitura não serve para validar a distância.
     */
    lowAccuracy: function() {
      return !!this.fence && !!this.position && !!this.position.accuracy && this.position.accuracy > this.fence;
    },

    // Só informativo: quem valida de fato a cerca eletrônica é o servidor.
    insideFence: function() {
      if (!this.fence) {
        return true;
      }

      return this.distance !== null && this.distance <= this.fence;
    },

    locationAllowsMark: function() {
      if (!this.fence) {
        return true;
      }

      return this.locationState === 'ok' && !this.lowAccuracy && !!this.companyCoordinates && this.insideFence;
    },

    locationAlert: function() {
      if (this.locationState === 'locating') {
        return { type: 'info', icon: 'mdi-crosshairs-gps', text: 'Obtendo a sua localização...' };
      }

      if (this.locationState === 'denied') {
        return {
          type: 'error',
          icon: 'mdi-map-marker-off',
          text: 'Permissão de localização negada.',
          detail: 'Libere o acesso à localização para este site nas configurações do navegador (ícone de cadeado ao lado do endereço) e clique em "Atualizar localização".'
        };
      }

      if (this.locationState === 'unavailable') {
        return { type: 'error', icon: 'mdi-map-marker-off', text: 'Este navegador não suporta geolocalização.' };
      }

      if (this.locationState === 'error') {
        return { type: 'error', icon: 'mdi-map-marker-off', text: 'Não foi possível obter a sua localização. Tente novamente.' };
      }

      if (this.locationState !== 'ok' || !this.position) {
        return { type: 'info', icon: 'mdi-map-marker-question', text: 'Localização ainda não obtida.' };
      }

      const accuracy = this.position.accuracy
        ? `Precisão do GPS: ${this.formatMeters(this.position.accuracy)}.${this.refining ? ' Refinando a localização...' : ''}`
        : null;

      if (this.lowAccuracy) {
        return {
          type: 'warning',
          icon: 'mdi-crosshairs-question',
          text: `Precisão do GPS insuficiente: margem de erro de ${this.formatMeters(this.position.accuracy)} (o limite é de ${this.fence} m).`,
          detail: `${this.refining ? 'Refinando a localização... ' : ''}Ative a localização precisa, conecte-se ao Wi-Fi ou use um aparelho com GPS e clique em "Atualizar localização".`
        };
      }

      if (!this.fence) {
        return { type: 'success', icon: 'mdi-map-marker-check', text: 'Localização obtida.', detail: accuracy };
      }

      if (!this.companyCoordinates) {
        return {
          type: 'warning',
          icon: 'mdi-map-marker-alert',
          text: 'O endereço da empresa não está geocodificado, por isso o Checkin não pode ser validado.',
          detail: 'Procure a coordenação para atualizar o cadastro da empresa.'
        };
      }

      if (this.insideFence) {
        return {
          type: 'success',
          icon: 'mdi-map-marker-check',
          text: `Você está a ${this.formatMeters(this.distance)} da empresa (limite de ${this.fence} m).`,
          detail: accuracy
        };
      }

      return {
        type: 'error',
        icon: 'mdi-map-marker-remove',
        text: `Você está a ${this.formatMeters(this.distance)} da empresa. O limite para o Checkin é de ${this.fence} m.`,
        detail: accuracy
      };
    },

    markTooltip: function() {
      if (this.isBlocked) {
        return 'Assine o(s) termo(s) obrigatório(s) pendentes para liberar o checkin';
      }

      if (!this.isClockValid) {
        return 'Aguarde sincronização do relógio ou preencha os dados';
      }

      if (!this.locationAllowsMark) {
        return 'Sua localização precisa estar dentro do raio permitido da empresa';
      }

      return 'Efetuar Checkin';
    },

    /**
     * Formata o horário do servidor para exibição.
     */
    formattedServerTime: function() {
      if (!this.serverTime) return null;
      try {
        const date = new Date(this.serverTime);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } catch {
        return null;
      }
    },

  },
  created() {
    this.token = this.$route.query.token || localStorage.getItem('token');
    this.currentToken = this.token;
    if (this.token) {
      this.$store.dispatch('auth/ActionSetToken', this.token);
    }
    this.getData();
    this.getLocation();
  },

  beforeDestroy() {
    this.stopWatch();
  },

  methods: {
    ...mapActions('timeSheet', ['ActionShowTimeSheet', 'ActionLogout', 'ActionCheckinInfoOccurrenceSheet', 'ActionCheckinOccurrenceSheet']),
    ...mapActions('auth', ['ActionTeacherLogout']),

    getData(){
      this.loading = true;
      this.teacher = null;
      this.institution  = null;
      this.sheet        = null;

      this.ActionShowTimeSheet({ token: this.token })
        .then((res) => {
          this.teacher      = res.data.teacher;
          this.institution  = res.data.institution;
          this.sheet        = res.data.sheet;
          this.clockStatus  = res.data.clock?.status || null;
          this.serverTime   = res.data.clock?.server_time || null;
          this.loadCheckin();
        })
        .catch(() => {
        })
        .finally(() => {
          this.loading = false;
        });
    },

    goBack() {
      this.$router.push({ name: 'teacher-panel' });
    },

    goToTimeSheet() {
      if (this.embedded) {
        this.$emit('open-timesheet')
        return
      }
      this.$router.push({ name: 'teacher-panel', query: { open: 'timesheet' } });
    },

    logout() {
      this.loadingLogout = true;

      this.ActionTeacherLogout({ token: this.token })
        .then(() => {
          this.$router.push({ path: '/login/teacher' });
        })
        .finally(() => {
          this.loadingLogout = false;
        });
    },
    
    mark(){
        
        this.loadingMark = true;
        this.error = false;
        this.message = '';

        // A marcação passa pelo serviço do Checkin: valida a cerca eletrônica (raio e precisão do
        // GPS) e grava a empresa da folha no comprovante. Sem raio configurado, funciona como antes.
        var payload = {
          id:           this.sheet.id,
          item_id:      this.sheet.items[0].id,
          latitude:     this.position ? this.position.lat : null,
          longitude:    this.position ? this.position.lng : null,
          accuracy:     this.position ? this.position.accuracy : null,
          device:       navigator.userAgent,
        };

        this.ActionCheckinOccurrenceSheet(payload)
            .then((res) => {
                this.message = res.message;
                
                // Exibir comprovante de registro de ponto se disponível
                if (res.data && res.data.receipt) {
                    this.receipt = res.data.receipt;
                    this.showReceipt = true;
                }
                
                this.getData();
            }).catch((error) => {
                this.error = true;
                // Tratar erro de relógio HLB específico
                if (error.response?.data?.errors?.clock) {
                    const clockError = error.response.data.errors.clock[0];
                    if (clockError === 'CLOCK_UNAVAILABLE') {
                        this.message = 'Sistema de relógio temporariamente indisponível. Tente novamente em alguns instantes.';
                    } else if (clockError === 'CLOCK_SKEW_EXCEEDED') {
                        this.message = 'O relógio do servidor está fora de sincronia. Tente novamente em alguns instantes.';
                    } else {
                        this.message = error.response?.data?.message || error.message;
                    }
                    // Atualizar dados para refletir novo status do clock
                    this.getData();
                } else {
                    this.message = error.response?.data?.message || error.message;
                }
            })
            .finally(() => {
              this.loadingMark = false;
              this.showMark = false;
            });
    },

    /**
     * Fecha o modal do comprovante de registro de ponto.
     */
    closeReceipt() {
        this.showReceipt = false;
        this.receipt = null;
    },

    /**
     * Dados do Checkin (cerca eletrônica, empresa e comprovantes das marcações de hoje).
     */
    loadCheckin() {
      this.checkin = null;

      if (!this.sheet || !this.sheet.id || !this.sheet.items || this.sheet.items.length === 0) {
        return;
      }

      this.ActionCheckinInfoOccurrenceSheet({ id: this.sheet.id })
        .then((res) => {
          this.checkin = res.data;
          this.isBlocked = !!res.data.blocked_by_terms;
        })
        .catch(() => {
          // Sem os dados do Checkin a tela continua funcionando; o servidor valida na marcação.
          this.checkin = null;
        });
    },

    /**
     * Usa watchPosition: a primeira leitura costuma ser aproximada (por IP/Wi-Fi) e o aparelho
     * refina em seguida. Mantém a leitura mais recente até ficar precisa ou esgotar o tempo.
     */
    getLocation() {
      this.stopWatch();

      if (!navigator.geolocation) {
        this.locationState = 'unavailable';
        return;
      }

      this.locationState = 'locating';
      this.position = null;
      this.refining = true;

      this.watchId = navigator.geolocation.watchPosition(
        (pos) => {
          this.position = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
          this.locationState = 'ok';

          if (pos.coords.accuracy <= GOOD_ACCURACY_METERS) {
            this.stopWatch();
          }
        },
        (err) => {
          // Se já existe uma leitura, só encerra o refinamento e mantém o que foi obtido.
          const hadPosition = !!this.position;

          this.stopWatch();

          if (!hadPosition) {
            this.locationState = err && err.code === 1 ? 'denied' : 'error';
          }
        },
        { enableHighAccuracy: true, timeout: MAX_REFINING_MS, maximumAge: 0 }
      );

      this.watchTimer = setTimeout(() => {
        this.stopWatch();

        if (!this.position && this.locationState === 'locating') {
          this.locationState = 'error';
        }
      }, MAX_REFINING_MS);
    },

    stopWatch() {
      if (this.watchId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(this.watchId);
      }

      if (this.watchTimer) {
        clearTimeout(this.watchTimer);
      }

      this.watchId = null;
      this.watchTimer = null;
      this.refining = false;
    },

    /**
     * A rota do PDF exige o token JWT; baixa com a requisição autenticada e abre a partir de um blob.
     */
    downloadReceipt(receipt) {
      if (!receipt || !receipt.pdf_url || this.downloadingId) {
        return;
      }

      this.downloadingId = receipt.id;

      this.$http.get(receipt.pdf_url, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.body], { type: 'application/pdf' });
          window.open((window.URL || window.webkitURL).createObjectURL(blob), '_blank');
        })
        .catch(() => {
          this.error = true;
          this.message = 'Não foi possível baixar o comprovante em PDF. Tente novamente.';
        })
        .finally(() => {
          this.downloadingId = null;
        });
    },

    formatTime(value) {
      return value ? String(value).substring(0, 5) : '--:--';
    },

    formatMeters(value) {
      const meters = Number(value);

      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
      }

      return `${Math.round(meters)} m`;
    },

    haversine(lat1, lng1, lat2, lng2) {
      const rad = (deg) => deg * Math.PI / 180;
      const dLat = rad(lat2 - lat1);
      const dLng = rad(lng2 - lng1);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;

      return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

  }
}